import { Typography } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from '../../core/AddButton';
import CancelButton from '../../core/CancelButton';
import Deprecated from '../../core/Deprecated';
import Errored from '../../core/Errored';
import Help from '../../core/Help';
import Loading from '../../core/Loading';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import SearchSelect from '../../core/SearchSelect';
import { globalStyles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import { getGraphFromProject, makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import AccessibleTooltip from '../../core/AccessibleTooltip';

const styles = Object.assign({}, globalStyles);

class AddClassDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            isErrored: null,
            inherit: false,
            ontologyProjectClasses: {},
            lang: props.defaultLang
        };
        this.cancellable = null;
    }

    componentDidMount() {
        this.collectProjectData();
    }

    loadData(iris, options) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, false));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    collectProjectData() {
        if (this.props.ontologyProjectAssociation && this.props.ontologyProjectAssociation.length > 0) {
            // At some point we want to filter the properties loaded for each component
            this.loadData(this.props.ontologyProjectAssociation)
                .promise
                .then(() => {
                    this.processProjects();
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isLoading: false });
                })
                .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                });
        }
        else {
            this.setState({ isLoading: false });
        }
    }

    processProjects() {
        let ontologyProjects = [];
        let ontologyProjectClasses = {};
        this.props.ontologyProjectAssociation.forEach(opa => {
            const opaData = GS.data(opa, this.props.graph);
            const projectGraph = getGraphFromProject(opaData.associatedProject);
            ontologyProjects.push(opaData.associatedProject);
            if (opaData.associatedClass) ontologyProjectClasses[projectGraph] = opaData.associatedClass;
        });
        this.setState({ ontologyProjectClasses, ontologyProjects });
    }

    handleClickOpen = () => this.setState({ open: true, lang: this.props.defaultLang, inherit: false });

    changeLanguage = (lang) => this.setState({ lang });

    computeSearch = (value, lang) => {
        let graph = this.state.ontologyProjects.map(project => getGraphFromProject(project));
        let ebp = [];
        this.props.exclude && this.props.exclude.forEach(item => {
            // Existing item disjoint with result
            ebp.push({
                predicate: [
                    "http://www.w3.org/2002/07/owl#disjointWith",
                    "https://graphifi.com/schema/xowl/hasSubClass"
                ],
                target: item,
                reverse: true,
                pathOperators: ["", "*"]
            });
            ebp.push({
                predicate: [
                    "http://www.w3.org/2002/07/owl#disjointWith",
                    "https://graphifi.com/schema/xowl/hasSubClass"
                ],
                target: item,
                pathOperators: ["", "*"]
            });
            // Result disjoint with existing item
            ebp.push({
                predicate: [
                    "http://www.w3.org/2000/01/rdf-schema#subClassOf",
                    "http://www.w3.org/2002/07/owl#disjointWith"
                ],
                target: item,
                pathOperators: ["*", ""]
            });
            ebp.push({
                predicate: [
                    "http://www.w3.org/2000/01/rdf-schema#subClassOf",
                    "http://www.w3.org/2002/07/owl#disjointWith"
                ],
                target: item,
                reverse: true,
                pathOperators: ["*", ""]
            });
        });
        // Don't allow classes that are subclass of concept scheme or collections
        ebp.push({
            predicate: [
                "http://www.w3.org/2000/01/rdf-schema#subClassOf"
            ],
            target: "http://www.w3.org/2004/02/skos/core#ConceptScheme",
            pathOperators: ["+"]
        });
        ebp.push({
            predicate: [
                "http://www.w3.org/2000/01/rdf-schema#subClassOf"
            ],
            target: "http://www.w3.org/2004/02/skos/core#Collection",
            pathOperators: ["+"]
        });
        ebp.push({
            predicate: [
                "http://www.w3.org/2000/01/rdf-schema#subClassOf"
            ],
            target: "http://www.w3.org/2004/02/skos/core#OrderedCollection",
            pathOperators: ["+"]
        });
        // Add in any additional subclass exclusions
        if (this.props.additionalEBP && this.props.additionalEBP.length > 0) {
            this.props.additionalEBP.forEach(aebp => {
                ebp.push({
                    predicate: [
                        "http://www.w3.org/2000/01/rdf-schema#subClassOf"
                    ],
                    target: aebp,
                    pathOperators: ["*", ""]
                });
            });
        }
        let classList = [];
        if (this.state.ontologyProjectClasses) {
            Object.keys(this.state.ontologyProjectClasses).forEach(op => classList = classList.concat(this.state.ontologyProjectClasses[op]));
        }
        let search = [
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: "http://www.w3.org/2002/07/owl#Class"
            }
        ];
        if (value !== "") {
            search.push({
                predicate: "http://www.w3.org/2000/01/rdf-schema#label",
                value: value,
                isExactMatch: false
            });
        }
        else {
            search.push({
                predicate: "http://www.w3.org/2000/01/rdf-schema#label"
            });
        }
        let fullSearch =
        {
            search,
            graph,
            from: classList,
            exclude: ["http://www.w3.org/2002/07/owl#Thing", "http://www.w3.org/2002/07/owl#Nothing", ...this.props.exclude],
            ...(this.props.exclude) && {
                ...(ebp.length > 0) && { excludeByPath: ebp }
            },
            language: lang,
            ignoreNoLanguage: lang !== "und",
            pageSize: 100,
            predicates: [
                "http://www.w3.org/2000/01/rdf-schema#label",
                "http://www.w3.org/2002/07/owl#deprecated",
                "http://www.w3.org/2002/07/owl#disjointWith"
            ]
        };
        return fullSearch;
    };

    addClass = (id) => {
        this.props.onAddClass(id, this.state.inherit);
        this.exitAddClass();
    };

    exitAddClass = () => this.setState({ open: false });

    handleChangeInherit = (event) => this.setState({ inherit: event.target.checked });

    render() {

        const { classes, exclude, offerInheritance, textRef } = this.props;
        const { isLoading, isErrored, ontologyProjectClasses, inherit, lang } = this.state;

        if (isErrored) return <Errored data={isErrored} />;
        if (isLoading) return <Loading />;
        if (Object.keys(ontologyProjectClasses).length === 0) return null;

        return (
            <React.Fragment>
                <AddButton dataTest="add-class" onClick={() => this.handleClickOpen()} title="Add a class" />
                <Dialog
                    maxWidth="md"
                    fullWidth
                    data-test="add-class-dialog"
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.mediumDialog
                    }}
                    className={classes.dialog}
                    open={this.state.open}
                    onClose={this.exitAddClass}
                    aria-labelledby="add-class-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="add-class-dialog-title">Add Class</DialogTitle>
                    <DialogContent style={{ height: 400, overflow: 'visible' }}>
                        {textRef && <Help textRef={textRef} />}
                        <form aria-label="Find a class" autoComplete="off" noValidate="novalidate">
                            {offerInheritance &&
                                <FormControlLabel
                                    data-test="option-label"
                                    control={
                                        <Checkbox data-test="inherit-class" checked={inherit} onChange={this.handleChangeInherit} name="inheritance" />}
                                    label="Add Class to Narrower Concepts"
                                    classes={{ label: classes.formLabelInline }}
                                />

                            }
                            <div style={{ height: 320 }}>
                                <TitledPanel title="Language">
                                    <ProjectLanguageSelector selectedLang={lang} includeLabel={false} onSelect={this.changeLanguage} />
                                </TitledPanel>
                                <SearchSelect
                                    key={lang}
                                    dataTest="find-class"
                                    focus={true}
                                    sortProp="label"
                                    exclude={this.props.exclude}
                                    wrapped={true}
                                    search={(value, lang) => this.computeSearch(value, lang)}
                                    listSecondIcon={(result) => {
                                        if (result.deprecated) return <Deprecated />;
                                        return null;
                                    }}
                                    listDisplay={(result, lang) => {
                                        const okay = ontologyProjectClasses[result.inNamedGraph] ?
                                            ontologyProjectClasses[result.inNamedGraph].includes(result.id)
                                            :
                                            false;
                                        if (result.label[lang]) {
                                            return <div>
                                                <Typography data-test="result-label" variant="body2">
                                                    <AccessibleTooltip placement="left" title={result.id}>
                                                        <span>
                                                            {result.label[lang]}
                                                        </span>
                                                    </AccessibleTooltip>
                                                </Typography>
                                                {!okay ?
                                                    Array.isArray(result.inNamedGraph) && result.inNamedGraph.length > 1 ?
                                                        <Typography variant="caption">This IRI is in more than one project.</Typography>
                                                        :
                                                        <Typography variant="caption">Not available</Typography>
                                                    : null}
                                            </div>;
                                        }
                                        else {
                                            return <Typography data-test="result-label" variant="body2">{result.id}</Typography>;
                                        }
                                    }}
                                    isClickableResult={(result) => {
                                        if (result.invalid) return false;
                                        if (result.deprecated) return false;
                                        if (result.disjointWith) {
                                            // Exclude will be all existing classes so we test not disjoint with any existing class
                                            const clash = result.disjointWith.every(item => !exclude.includes(item));
                                            if (!clash) return false;
                                        }
                                        const okay = ontologyProjectClasses[result.inNamedGraph] ?
                                            ontologyProjectClasses[result.inNamedGraph].includes(result.id)
                                            :
                                            false;
                                        return okay;
                                    }}
                                    title="Filter classes"
                                    lang={lang}
                                    chosen={this.addClass} />
                            </div>
                        </form>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitAddClass} />
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

AddClassDialog.defaultProps = {
    offerInheritance: false,
    exclude: [],
    additionalEBP: [
        "http://www.w3.org/2008/05/skos-xl#Label"
    ]
};

AddClassDialog.propTypes = {
    classes: PropTypes.object,
    schemeIRI: PropTypes.string,
    textRef: PropTypes.string,
    offerInheritance: PropTypes.bool,
    ontologyProjectAssociation: PropTypes.array,
    exclude: PropTypes.array,
    graph: PropTypes.string.isRequired,
    onAddClass: PropTypes.func.isRequired,
    viewLang: PropTypes.string,
    defaultLang: PropTypes.string.isRequired
};

export default withStyles(styles)(AddClassDialog);